import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDbtModels } from '../../hooks/useDbtData';
import LoadingSpinner from '../common/LoadingSpinner';
import SearchInput from '../common/SearchInput';
import { dbtConfig } from '../../config/dbt';

/**
 * DbtModelExplorer Component
 * Searchable, filterable table of dbt models
 */
const DbtModelExplorer = ({ projectId }) => {
  const { data: models, isLoading, error } = useDbtModels(projectId);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMaterialization, setSelectedMaterialization] = useState('');

  // Filter models based on search and materialization
  const filteredModels = useMemo(() => {
    if (!models) return [];

    return models.filter((model) => {
      const matchesSearch =
        model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (model.description && model.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (model.schema_name && model.schema_name.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesMaterialization =
        !selectedMaterialization || model.materialization === selectedMaterialization;

      return matchesSearch && matchesMaterialization;
    });
  }, [models, searchTerm, selectedMaterialization]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading models: {error.message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search models..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={selectedMaterialization}
          onChange={(e) => setSelectedMaterialization(e.target.value)}
          className="select select-bordered w-full md:w-auto"
        >
          <option value="">All Materializations</option>
          {dbtConfig.materializations.map((mat) => (
            <option key={mat.value} value={mat.value}>
              {mat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div className="text-sm text-base-content/70">
        Showing {filteredModels.length} of {models?.length || 0} models
      </div>

      {/* Models Table */}
      <div className="overflow-x-auto bg-white shadow-xl rounded-lg">
        <table className="table">
          <thead>
            <tr>
              <th>Model Name</th>
              <th>Schema</th>
              <th>Materialization</th>
              <th>Description</th>
              <th>Dependencies</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredModels.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-base-content/60">
                  {searchTerm || selectedMaterialization
                    ? 'No models match your filters'
                    : 'No models found'}
                </td>
              </tr>
            ) : (
              filteredModels.map((model) => (
                <tr key={model.id} className="hover">
                  <td>
                    <div className="font-medium">{model.name}</div>
                    <div className="text-xs text-base-content/60">
                      {model.database_name}.{model.schema_name}.{model.name}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-ghost badge-sm">
                      {model.schema_name}
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-primary badge-sm">
                      {model.materialization}
                    </span>
                  </td>
                  <td>
                    <div className="max-w-md truncate text-sm">
                      {model.description || (
                        <span className="text-base-content/40 italic">No description</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="text-sm">
                      {model.depends_on?.length || 0} {model.depends_on?.length === 1 ? 'dependency' : 'dependencies'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Link
                        to={`/dbt/projects/${projectId}/models/${model.id}`}
                        className="btn btn-xs btn-primary"
                      >
                        View
                      </Link>
                      <Link
                        to={`/dbt/projects/${projectId}/models/${model.id}/lineage`}
                        className="btn btn-xs btn-ghost"
                      >
                        Lineage
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Tags Filter (if models have tags) */}
      {models && models.some(m => m.tags && m.tags.length > 0) && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">Available Tags:</h4>
          <div className="flex flex-wrap gap-2">
            {Array.from(
              new Set(models.flatMap(m => m.tags || []))
            ).map(tag => (
              <button
                key={tag}
                className="badge badge-outline"
                onClick={() => setSearchTerm(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DbtModelExplorer;
